/**
 * @fileoverview POD Claims Panel — data table of Proof of Debt claims.
 *
 * Purpose: Lists every POD claim on the job as a creditor schedule — the
 * books-and-records estimate of what is owed beside the proof as lodged, split
 * into the classes it ranks in (security held, preferential, unsecured),
 * with lifecycle status and adjudication outcome. Rows open the claim
 * detail slide-over for line items and adjudication.
 */

import { useEffect, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import type { Id } from "@cvx/_generated/dataModel";
import { Button } from "@/ui/primitives/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { ColumnVisibilityMenu } from "@/ui/composed/data-display/column-visibility-menu";
import { Loader2, Scale, Plus } from "lucide-react";
import { cn } from "@/utils/misc";
import {
  POD_STATUS_LABELS,
  type PodClaimStatus,
  type PodClaimUI,
} from "./podShared";
import {
  POD_CLAIMS_EXPORT_COLUMN,
  POD_CLAIMS_SELECT_COLUMN,
  POD_CLAIMS_STICKY_COLUMN,
  makePodClaimsColumns,
} from "./podClaimsColumns";
import { PodClaimExportMenu } from "./PodClaimExportMenu";
import { useNewOutputBoundary } from "../_shared/use-new-output-boundary";
import { POD_INTAKE_AGENT_KEY } from "./recoveriesRegistry";

const HEAD_CLASS =
  "h-10 text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap";

/**
 * The selection checkbox and the creditor column stay put while the rest of
 * the schedule scrolls sideways — a row of money columns means nothing
 * without the name against it. The checkbox pins at the left edge and the
 * creditor pins just after it, so the shadow marking the scroll boundary
 * belongs to the creditor column alone.
 */
const STICKY_SELECT_HEAD = "sticky left-0 z-20 bg-background w-10";
const STICKY_SELECT_CELL = "sticky left-0 z-10 bg-background w-10";
const STICKY_HEAD =
  "sticky left-10 z-20 bg-background min-w-[220px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]";
const STICKY_CELL =
  "sticky left-10 z-10 bg-background min-w-[220px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]";

/** Hidden columns persist per user, as on the contacts table. */
const STORAGE_KEY = "pod-claims-hidden-cols";

function loadVisibility(): VisibilityState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as VisibilityState;
  } catch {
    /* ignore */
  }
  return {};
}

export function PodClaimsPanel({
  jobId,
  claims,
  isLoading,
  selectedClaimId,
  onSelect,
  onAdd,
}: {
  jobId: Id<"jobs">;
  claims: PodClaimUI[];
  isLoading: boolean;
  selectedClaimId: Id<"podClaims"> | null;
  onSelect: (id: Id<"podClaims">) => void;
  onAdd: () => void;
}) {
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [sorting, setSorting] = useState<SortingState>([
    { id: "totalProof", desc: true },
  ]);
  const [columnVisibility, setColumnVisibility] =
    useState<VisibilityState>(loadVisibility);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  useEffect(() => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(columnVisibility),
      );
    } catch {
      /* ignore */
    }
  }, [columnVisibility]);

  const newSince = useNewOutputBoundary(jobId, POD_INTAKE_AGENT_KEY);
  const columns = useMemo(() => makePodClaimsColumns(newSince), [newSince]);

  const filtered = useMemo(
    () =>
      statusFilter ? claims.filter((c) => c.status === statusFilter) : claims,
    [claims, statusFilter],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting, columnVisibility, rowSelection },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (claim) => claim._id,
  });

  // Ticked rows in schedule order, over the UNFILTERED claims so a selection
  // survives a status-filter change rather than silently shrinking.
  const selectedClaimIds = useMemo(
    () => claims.filter((c) => rowSelection[c._id]).map((c) => c._id),
    [claims, rowSelection],
  );

  // -------------------------------------------------------------------------
  // Loading / empty states
  // -------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-3 py-16">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
        <p className="text-sm text-muted-foreground">Loading POD claims...</p>
      </div>
    );
  }

  if (claims.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-background py-16">
        <div className="rounded-full bg-muted p-3">
          <Scale className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">No POD claims yet.</p>
        <p className="max-w-md text-center text-xs text-muted-foreground">
          Lodge a claim for each creditor when their Proof of Debt (Form 535) is
          requested or received. Claims linked to the creditors table surface
          their POD status there too.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-2 gap-1.5"
          onClick={onAdd}
        >
          <Plus className="h-3.5 w-3.5" />
          New POD Claim
        </Button>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="min-w-0 overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
      {/* Filter bar */}
      <div className="flex items-end gap-3 border-b border-border px-5 py-3">
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Status
          </label>
          <Select
            value={statusFilter || "all"}
            onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}
          >
            <SelectTrigger className="h-8 min-w-[130px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {(
                Object.entries(POD_STATUS_LABELS) as Array<
                  [PodClaimStatus, string]
                >
              ).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex items-center gap-2 pb-0.5">
          {selectedClaimIds.length > 0 ? (
            <>
              <span className="text-xs text-muted-foreground">
                {selectedClaimIds.length} selected
              </span>
              <PodClaimExportMenu
                jobId={claims[0].jobId}
                claimIds={selectedClaimIds}
                label={`Export selected (${selectedClaimIds.length})`}
              />
            </>
          ) : (
            <PodClaimExportMenu
              jobId={claims[0].jobId}
              claimIds={table.getRowModel().rows.map((r) => r.original._id)}
            />
          )}
          <ColumnVisibilityMenu table={table} />
        </div>
      </div>

      {/* Data table. Ten columns will not fit a laptop viewport, so the table
          is given a minimum width and allowed to scroll sideways inside the
          card, with the creditor pinned so a row stays identifiable. */}
      <div className="max-h-[calc(100vh-480px)] overflow-auto">
        <Table className="w-full min-w-[1200px]">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className="bg-muted/30 hover:bg-transparent"
              >
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      HEAD_CLASS,
                      header.column.id === "status" && "min-w-[120px]",
                      header.column.id === POD_CLAIMS_SELECT_COLUMN &&
                        STICKY_SELECT_HEAD,
                      header.column.id === POD_CLAIMS_STICKY_COLUMN &&
                        STICKY_HEAD,
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={table.getVisibleFlatColumns().length}
                  className="h-32 text-center"
                >
                  <span className="text-sm text-muted-foreground">
                    No claims match the selected filter.
                  </span>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => {
                const isSelected = selectedClaimId === row.original._id;
                return (
                  <TableRow
                    key={row.id}
                    className={cn(
                      "cursor-pointer transition-colors",
                      isSelected
                        ? "hover:bg-primary/8 bg-primary/5"
                        : "hover:bg-muted/50",
                    )}
                    onClick={() => onSelect(row.original._id)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          "px-4 py-3 align-top",
                          cell.column.id === "status" && "min-w-[120px]",
                          cell.column.id === POD_CLAIMS_SELECT_COLUMN &&
                            STICKY_SELECT_CELL,
                          cell.column.id === POD_CLAIMS_EXPORT_COLUMN &&
                            "w-8 py-1 pr-2",
                          cell.column.id === POD_CLAIMS_STICKY_COLUMN &&
                            STICKY_CELL,
                          // A sticky cell paints its own opaque background, so
                          // it has to repeat the row's selection tint.
                          (cell.column.id === POD_CLAIMS_STICKY_COLUMN ||
                            cell.column.id === POD_CLAIMS_SELECT_COLUMN) &&
                            isSelected &&
                            "!bg-primary/5",
                        )}
                        // The checkbox and the export menu must not open the
                        // claim slide-over the row click is wired to.
                        onClick={
                          cell.column.id === POD_CLAIMS_SELECT_COLUMN ||
                          cell.column.id === POD_CLAIMS_EXPORT_COLUMN
                            ? (e) => e.stopPropagation()
                            : undefined
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Footer hint */}
      <div className="border-t border-border px-5 py-3">
        <p className="text-center text-xs text-muted-foreground">
          Click any claim to review line items and record the adjudication
        </p>
      </div>
    </div>
  );
}
